import { createRouter, createWebHistory } from 'vue-router';

import Home from '../views/index.vue';
import store from '../store';
import { handleDynamicImportChunkError, lazyRoute } from '@/utils/chunkLoadError';
import { resolvePathWhenRedirectIsRoot } from '@/utils/teacherHomePath';
import { useUserDashboardPreferencesStore } from '@/stores/userDashboardPreferences';

// Vue.use(VueRouter);
// import { createApp } from 'vue';
// const app = createApp();
// app.use(createRouter);

const routes = [
    //dashboard
    {   path: '/', 
        name: 'Home', 
        component: Home,
        meta: { layout: 'app', requiresAuth: true },
        async beforeEnter(to, from, next) {
            const prefs = useUserDashboardPreferencesStore();
            await prefs.fetchPreferences();
            if (prefs.isNewDashboardActive) {
                next({ name: 'dashboard' });
                return;
            }
            next();
        },
    },

    {
        path: '/dashboard-educador-digital',
        name: 'dashboard-educador-digital',
        component: lazyRoute(() => import(/* webpackChunkName: "dashboard-educador-digital" */ '../views/DashboardEducadorDigital.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/dashboard',
        name: 'dashboard',
        component: lazyRoute(() => import(/* webpackChunkName: "dashboard" */ '../views/dashboard-v2/NovoDashboard.vue')),
        meta: { layout: 'app', requiresAuth: true },
        async beforeEnter(to, from, next) {
            const prefs = useUserDashboardPreferencesStore();
            await prefs.fetchPreferences({ force: true });
            if (!prefs.available) {
                next({ name: 'Home' });
                return;
            }
            if (!prefs.isNewDashboardActive) {
                next({ name: 'Home' });
                return;
            }
            next();
        },
    },

    // Compatibilidade com URL antiga do piloto
    {
        path: '/dashboard-v2',
        redirect: '/dashboard',
    },

    {
        path: '/financeiro',
        name: 'financeiro',
        component: lazyRoute(() => import(/* webpackChunkName: "financeiro" */ '../views/financeiro.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/documentos',
        name: 'documentos',
        component: lazyRoute(() => import(/* webpackChunkName: "anamneses" */ '../views/documentos/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/revisoes-texto',
        name: 'revisoes-texto',
        component: lazyRoute(() => import(/* webpackChunkName: "revisoes-texto" */ '../views/revisoes-texto/lista.vue')),
        meta: { layout: 'app', requiresAuth: true },
        async beforeEnter(to, from, next) {
            const { useUserStore } = await import('@/stores/user');
            const userStore = useUserStore();
            try {
                await userStore.getUser();
            } catch {
                /* ignorar */
            }
            const authUser = store.state.auth?.user;
            const v =
                userStore.user?.aceita_revisoes_texto_terceiros ??
                authUser?.aceita_revisoes_texto_terceiros;
            const ok = v === true || v === 1 || v === '1';
            if (!ok) {
                next({ name: 'Home' });
                return;
            }
            next();
        },
    },

    {
        path: '/documento/preview/:id',
        name: 'documento-preview',
        component: lazyRoute(() => import(/* webpackChunkName: "documento-preview" */ '../views/documentos/PreviewRoute.vue')),
    },

    {
        path: '/documento/:id?',
        name: 'documento',
        component: lazyRoute(() => import(/* webpackChunkName: "anamnese-adicionar" */ '../views/documentos/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/aluno/:id?',
        name: 'cadastro-paciente',
        component: lazyRoute(() => import(/* webpackChunkName: "pacientes-cadastro" */ '../views/pacientes/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/aluno/detalhes/:id?',
        name: 'detalhes-paciente',
        component: lazyRoute(() => import(/* webpackChunkName: "pacientes-detalhes" */ '../views/pacientes/detalhes.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/alunos',
        name: 'lista',
        component: lazyRoute(() => import(/* webpackChunkName: "pacientes-lista" */ '../views/pacientes/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/turmas',
        name: 'turmas',
        component: lazyRoute(() => import(/* webpackChunkName: "turmas" */ '../views/turmas/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    
    {
        path: '/turma/:id?',
        name: 'turma',
        component: lazyRoute(() => import(/* webpackChunkName: "turma-adicionar" */ '../views/turmas/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    {
        path: '/turma/visualizar/:id',
        name: 'turma-visualizar',
        component: lazyRoute(() => import(/* webpackChunkName: "turma-visualizar" */ '../views/turmas/show.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    {
        path: '/atividades',
        name: 'atividades',
        component: lazyRoute(() => import(/* webpackChunkName: "atividades" */ '../views/atividades/lista.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/atividade/:id?',
        name: 'atividade',
        component: lazyRoute(() => import(/* webpackChunkName: "atividade-form" */ '../views/atividades/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    {
        path: '/temas',
        name: 'temas',
        component: lazyRoute(() => import(/* webpackChunkName: "temas" */ '../views/temas/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    
    {
        path: '/tema/:id?',
        name: 'tema',
        component: lazyRoute(() => import(/* webpackChunkName: "tema-adicionar" */ '../views/temas/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },


    //auth
    {
        path: '/auth/confirmar-email',
        name: 'confirm-email-boxed',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-pass-recovery-boxed" */ '../views/auth/confirm_email_boxed.vue')),
        meta: { layout: 'auth' },
    },
    {
        path: '/auth/completar-cadastro',
        name: 'complet-register',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-pass-recovery-boxed" */ '../views/auth/completar_cadastro.vue')),
        meta: { layout: 'auth', requiresAuth: true },
    },
    {
        path: '/bem-vindo',
        name: 'bem-vindo',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-pass-recovery-boxed" */ '../views/auth/bem_vindo_page.vue')),
        meta: { layout: 'auth', requiresAuth: true },
    },
    {
        path: '/auth/login',
        name: 'login',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-login" */ '../views/auth/login.vue')),
        meta: { layout: 'auth' },
    },
    {
        path: '/portal/:slug',
        redirect: (to) => ({ name: 'portal-login', params: { slug: to.params.slug } }),
    },
    {
        path: '/portal/:slug/login',
        name: 'portal-login',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-login" */ '../views/auth/login.vue')),
        meta: { layout: 'auth' },
    },
    // Compatibilidade com links antigos /p/{slug}
    {
        path: '/p/:slug',
        redirect: (to) => ({ name: 'portal-login', params: { slug: to.params.slug } }),
    },
    {
        path: '/p/:slug/login',
        redirect: (to) => ({ name: 'portal-login', params: { slug: to.params.slug } }),
    },
    {
        path: '/auth/register',
        name: 'register',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-register" */ '../views/auth/register.vue')),
        meta: { layout: 'auth' },
    },
    {
        path: '/auth/google/callback',
        name: 'google-callback',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-google-callback" */ '../views/auth/google-callback.vue')),
        meta: { layout: 'auth' },
    },

    {
        path: '/auth/recuperar-senha',
        name: 'recuperar-senha',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-recuperar-senha" */ '../views/auth/recuperar_senha.vue')),
        meta: { layout: 'auth' },
    },
    {
        path: '/portal/:slug/recuperar-senha',
        name: 'portal-recuperar-senha',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-recuperar-senha" */ '../views/auth/recuperar_senha.vue')),
        meta: { layout: 'auth' },
    },
    {
        path: '/p/:slug/recuperar-senha',
        redirect: (to) => ({ name: 'portal-recuperar-senha', params: { slug: to.params.slug }, query: to.query }),
    },

    {
        path: '/auth/redefinir-senha',
        name: 'redefinir-senha',
        component: lazyRoute(() => import(/* webpackChunkName: "auth-pass-recovery-change" */ '../views/auth/recuperar_senha_alterar.vue')),
        meta: { layout: 'auth' },
    },

    {
        path: '/termos-de-servico',
        name: 'termos-de-servico',
        component: lazyRoute(() => import(/* webpackChunkName: "termos-de-servico" */ '../views/termos_de_servico.vue')),
        meta: { layout: 'auth' }
    },

    {
        path: '/politica-de-privacidade',
        name: 'politica-de-privacidade',
        component: lazyRoute(() => import(/* webpackChunkName: "politica-de-privacidade" */ '../views/politica_privacidade.vue')),
        meta: { layout: 'auth' }
    },

    {
        path: '/aceite-legal',
        name: 'aceite-legal',
        component: lazyRoute(() => import(/* webpackChunkName: "aceite-legal" */ '../views/legal/aceite_legal.vue')),
        meta: { layout: 'auth', requiresAuth: true }
    },

    {
        path: '/contato',
        redirect: { path: '/ajuda', query: { tab: 'contato' } },
    },

    //users
    {
        path: '/meus-dados',
        name: 'profile',
        component: lazyRoute(() => import(/* webpackChunkName: "users-profile" */ '../views/users/profile.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/landing-page',
        name: 'landing-page-gerenciar',
        component: lazyRoute(() => import(/* webpackChunkName: "landing-page-gerenciar" */ '../views/landing-page/GerenciarLandingPage.vue')),
        meta: { layout: 'app', requiresAuth: true },
        async beforeEnter(to, from, next) {
            const { useUserStore } = await import('@/stores/user');
            const { resolveIsAluno } = await import('@/utils/professorRole');
            const userStore = useUserStore();
            try {
                await userStore.getUser();
            } catch {
                /* ignorar */
            }
            const user = userStore.user || store.state.auth?.user;
            if (resolveIsAluno(user)) {
                next({ name: 'Home' });
                return;
            }
            next();
        },
    },
    {
        path: '/identidade-visual',
        name: 'identidade-visual',
        component: lazyRoute(() => import(/* webpackChunkName: "identidade-visual" */ '../views/branding/IdentidadeVisual.vue')),
        meta: { layout: 'app', requiresAuth: true },
        async beforeEnter(to, from, next) {
            const { useUserStore } = await import('@/stores/user');
            const { resolveIsAluno } = await import('@/utils/professorRole');
            const userStore = useUserStore();
            try {
                await userStore.getUser();
            } catch {
                /* ignorar */
            }
            const user = userStore.user || store.state.auth?.user;
            if (resolveIsAluno(user)) {
                next({ name: 'Home' });
                return;
            }
            next();
        },
    },
    {
        path: '/user/cadastro',
        name: 'cadastro-user',
        component: lazyRoute(() => import(/* webpackChunkName: "cadastro-user" */ '../views/users/cadastro.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/users/account-setting',
        name: 'account-setting',
        component: lazyRoute(() => import(/* webpackChunkName: "users-account-setting" */ '../views/users/account_setting.vue')),
    },
    {
        path: '/configuracoes/notificacoes',
        redirect: '/preferencias',
    },
    {
        path: '/preferencias',
        name: 'preferencias',
        component: lazyRoute(() => import(/* webpackChunkName: "preferencias" */ '../views/users/configuracoes-notificacoes.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/configuracoes/creditos',
        name: 'creditos-configuracoes',
        component: lazyRoute(() => import(/* webpackChunkName: "creditos-config" */ '../views/credits/configuracoes.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao',
        name: 'modelos-avaliacao',
        component: lazyRoute(() => import(/* webpackChunkName: "modelos-avaliacao" */ '../views/modelos-avaliacao/lista.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao/:id/cores',
        name: 'modelo-avaliacao-cores',
        redirect: (to) => ({
            name: 'modelo-avaliacao-marcadores',
            params: { id: to.params.id },
        }),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao/:id/marcadores',
        name: 'modelo-avaliacao-marcadores',
        component: lazyRoute(() => import(/* webpackChunkName: "modelo-avaliacao-marcadores" */ '../views/modelos-avaliacao/marcadores.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao/:id/comentarios-rapidos',
        name: 'modelo-avaliacao-comentarios-rapidos',
        component: lazyRoute(() => import(/* webpackChunkName: "modelo-avaliacao-comentarios-rapidos" */ '../views/modelos-avaliacao/comentarios-rapidos.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao/:id/intervalos',
        name: 'modelo-avaliacao-intervalos',
        component: lazyRoute(() => import(/* webpackChunkName: "modelo-avaliacao-intervalos" */ '../views/modelos-avaliacao/intervalos.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/modelos-avaliacao/:id',
        name: 'modelo-avaliacao-personalizar',
        component: lazyRoute(() => import(/* webpackChunkName: "modelo-avaliacao-personalizar" */ '../views/modelos-avaliacao/personalizar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    //planos
    {
        path: '/plano',
        name: 'plano',
        component: lazyRoute(() => import(/* webpackChunkName: "plano" */ '../views/planos/index.vue')),
    },

    //checkout
    {
        path: '/checkout/:id',
        name: 'checkout',
        component: lazyRoute(() => import(/* webpackChunkName: "checkout" */ '../views/faturas/preview.vue')),
    },

     //checkout
     {
        path: '/faturas',
        name: 'faturas',
        component: lazyRoute(() => import(/* webpackChunkName: "list" */ '../views/faturas/list.vue')),
    },

    //prompts
    {
        path: '/prompts',
        name: 'prompts',
        component: lazyRoute(() => import(/* webpackChunkName: "prompts" */ '../views/prompts/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/prompt/:id?',
        name: 'prompt',
        component: lazyRoute(() => import(/* webpackChunkName: "prompt-adicionar" */ '../views/prompts/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    //venda
    {
        path: '/inscricao/turma/:id?',
        name: 'inscricao-turma',
        component: lazyRoute(() => import(/* webpackChunkName: "inscricao-turma" */ '../views/venda/turma.vue')),
        meta: { layout: 'auth' },
    },

    {
        path: '/inscricao/confirmar/:id?',
        name: 'inscricao-turma-confirmar',
        component: lazyRoute(() => import(/* webpackChunkName: "inscricao-turma" */ '../views/venda/preview.vue')),
        meta: { layout: 'auth' },
    },

    {
        path: '/inscricao/sucesso/:id?',
        name: 'inscricao-turma-sucesso',
        component: lazyRoute(() => import(/* webpackChunkName: "inscricao-turma" */ '../views/venda/sucesso.vue')),
        meta: { layout: 'auth' },
    },

    {
        path: '/relatorios',
        name: 'relatorios',
        component: lazyRoute(() => import(/* webpackChunkName: "relatorios" */ '../views/relatorios/index.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    //planos de estudos
    {
        path: '/planos-estudos',
        name: 'planos-estudos',
        component: lazyRoute(() => import(/* webpackChunkName: "planos-estudos" */ '../views/study-plans/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/plano-estudo/criar/:id?',
        name: 'plano-estudo-criar',
        component: lazyRoute(() => import(/* webpackChunkName: "plano-estudo-criar" */ '../views/study-plans/criar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/plano-estudo/:id',
        name: 'plano-estudo',
        component: lazyRoute(() => import(/* webpackChunkName: "plano-estudo-cadastrar" */ '../views/study-plans/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/planos-estudo/entrar/:token',
        name: 'planos-estudo-entrar-link',
        component: lazyRoute(() => import(/* webpackChunkName: "planos-estudo-entrar-link" */ '../views/study-plans/entrar-por-link.vue')),
        meta: { layout: 'auth', requiresAuth: false }
    },

    //cursos
    {
        path: '/cursos',
        name: 'cursos-lista',
        component: lazyRoute(() => import(/* webpackChunkName: "cursos-lista" */ '../views/courses/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/cursos/entrar/:token',
        name: 'cursos-entrar-link',
        component: lazyRoute(() => import(/* webpackChunkName: "cursos-entrar-link" */ '../views/courses/entrar-por-link.vue')),
        meta: { layout: 'auth', requiresAuth: false }
    },
    {
        path: '/curso/:id?',
        name: 'curso',
        component: lazyRoute(() => import(/* webpackChunkName: "curso-cadastrar" */ '../views/courses/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/curso/:id/gerenciar',
        name: 'curso-gerenciar',
        component: lazyRoute(() => import(/* webpackChunkName: "curso-gerenciar" */ '../views/courses/gerenciar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/curso/:id/analytics',
        name: 'curso-analytics',
        component: lazyRoute(() => import(/* webpackChunkName: "curso-analytics" */ '../views/courses/analytics.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/aula/:lessonId/quiz',
        name: 'quiz-gerenciar',
        component: lazyRoute(() => import(/* webpackChunkName: "quiz-gerenciar" */ '../views/courses/quiz.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/recurso/:resourceId/quiz',
        name: 'recurso-quiz-gerenciar',
        component: lazyRoute(() => import(/* webpackChunkName: "quiz-gerenciar" */ '../views/courses/quiz.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/plano-estudo/tarefa/:taskId/recurso/:resourceId/quiz',
        name: 'quiz-aluno',
        component: lazyRoute(() => import(/* webpackChunkName: "quiz-aluno" */ '../views/study-plans/QuizAluno.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    //recursos
    {
        path: '/recursos',
        name: 'recursos-lista',
        component: lazyRoute(() => import(/* webpackChunkName: "recursos-lista" */ '../views/recursos/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/recursos/cadastrar',
        name: 'recursos-cadastrar',
        component: lazyRoute(() => import(/* webpackChunkName: "recursos-cadastrar" */ '../views/recursos/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/recurso/:id?',
        name: 'recurso',
        component: lazyRoute(() => import(/* webpackChunkName: "recurso" */ '../views/recursos/editar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    // Meus corretores (pool do professor)
    {
        path: '/corretores',
        name: 'corretores',
        component: lazyRoute(() => import(/* webpackChunkName: "corretores" */ '../views/corretores/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    //checklists
    {
        path: '/checklists',
        name: 'checklists',
        component: lazyRoute(() => import(/* webpackChunkName: "checklists" */ '../views/checklists/lista.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },
    {
        path: '/checklist/:id?',
        name: 'checklist',
        component: lazyRoute(() => import(/* webpackChunkName: "checklist-cadastrar" */ '../views/checklists/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true }
    },

    {
        path: '/marcacoes-ia-presets',
        name: 'marcacoes-ia-presets',
        component: lazyRoute(() => import(/* webpackChunkName: "marcacoes-ia-presets" */ '../views/marcacoes-ia-presets/lista.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },
    {
        path: '/marcacoes-ia-preset/:id?',
        name: 'marcacoes-ia-preset',
        component: lazyRoute(() => import(/* webpackChunkName: "marcacoes-ia-preset" */ '../views/marcacoes-ia-presets/cadastrar.vue')),
        meta: { layout: 'app', requiresAuth: true },
    },

    //helpdesk (acessível sem login; menu lateral fechado por padrão)
    {
        path: '/ajuda',
        name: 'ajuda',
        component: lazyRoute(() => import(/* webpackChunkName: "ajuda" */ '../views/helpdesk/helpdesk.vue')),
        meta: { layout: 'auth', requiresAuth: false }
    },
    {
        path: '/documentacao',
        redirect: { path: '/ajuda', query: { tab: 'doc', section: 'visao-geral' } },
    },
    {
        path: '/perguntas-frequentes',
        redirect: { path: '/ajuda', query: { tab: 'faq' } },
    },
    {
        path: '/suporte',
        redirect: { path: '/ajuda', query: { tab: 'contato' } },
    },
    {
        path: '/tutoriais',
        redirect: { path: '/ajuda', query: { tab: 'tutoriais' } },
    },
    {
        path: '/assistente',
        redirect: { path: '/ajuda', query: { tab: 'assistente' } },
    },

    // 404 — deve permanecer por último (Vue Router 4); layout auth = sem header/sidebar
    {
        path: '/:pathMatch(.*)*',
        name: 'not-found',
        component: lazyRoute(() => import(/* webpackChunkName: "not-found" */ '../views/NotFound.vue')),
        meta: { layout: 'auth', requiresAuth: false },
    },

];

const router = createRouter({
    history: createWebHistory(),
    linkExactActiveClass: 'active',
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (typeof window === "undefined") return false;

        // Se há uma posição salva (navegação com botão voltar/avançar), use ela
        if (savedPosition) {
            return savedPosition;
        }

        if (to.name === 'not-found') {
            return { top: 0, left: 0 };
        }

        // Para recarregamento de página ou navegação direta, não force scroll
        // Deixa o navegador manter a posição natural
        return false;
    },
});

const PUBLIC_LEGAL_DOCUMENT_ROUTES = new Set(['termos-de-servico', 'politica-de-privacidade']);

function isPublicLegalDocumentRoute(to) {
    const path = (to.path || '').replace(/\/$/, '') || '/';
    return (
        path === '/termos-de-servico' ||
        path === '/politica-de-privacidade' ||
        PUBLIC_LEGAL_DOCUMENT_ROUTES.has(to.name)
    );
}

router.beforeEach(async (to, from, next) => {
    if (isPublicLegalDocumentRoute(to)) {
        store.commit('setLoading', false);
    } else {
        store.commit('setLoading', true);
    }

    if (!requiresAuth(to)) {
        // Não chama checkAuth nas telas de autenticação: evita GET /user concorrente ao POST /login
        // (401 em paralelo limpava a sessão logo após login bem-sucedido).
        const isAuthScreen =
            to.name === 'login' ||
            to.name === 'portal-login' ||
            to.name === 'register' ||
            to.name === 'recuperar-senha' ||
            to.name === 'redefinir-senha' ||
            to.name === 'google-callback' ||
            to.name === 'termos-de-servico' ||
            to.name === 'politica-de-privacidade';

        const isGuestOnlyAuthScreen =
            to.name === 'login' || to.name === 'portal-login' || to.name === 'register';

        // Rotas públicas (ex.: /ajuda): ainda tenta carregar auth para menu quando já houver sessão/token.
        try {
            const alreadyAuthenticated = store.getters['auth/isAuthenticated'];
            if (isGuestOnlyAuthScreen) {
                // Sessão Sanctum pode existir sem Vuex hidratado; evita POST guest (409) em login/register.
                const user = alreadyAuthenticated
                    ? store.state.auth.user
                    : await store.dispatch('auth/checkAuth', { logoutOnFail: false });
                if (user) {
                    const prefs = useUserDashboardPreferencesStore();
                    await prefs.fetchPreferences();
                    return next(resolvePathWhenRedirectIsRoot('/', user, prefs));
                }
            } else if (!isAuthScreen && !alreadyAuthenticated) {
                await store.dispatch('auth/checkAuth');
            }
        } catch (e) {
            // Ignora erro (usuário não logado); a rota é acessível mesmo assim
        }
        // Na Central de Ajuda: sidebar já entra fechado e sem animação
        if (to.name === 'ajuda') {
            store.commit('setSidebarSkipTransition', true);
            store.commit('toggleSideBarAuto', false);
        }
        return next();
    }

    try {
        const alreadyAuthenticated = store.getters['auth/isAuthenticated'];
        if (!alreadyAuthenticated) {
            await store.dispatch('auth/checkAuth');
        }
        const isAuthenticated = store.getters['auth/isAuthenticated'];

        if (!isAuthenticated) {
            console.log('Not authenticated, redirecting to login');
            return redirectToLogin(to, next);
        }

        if (needsConfirmeEmail(to)) {
            console.log('Needs email confirmation, redirecting');
            return redirectToConfirmeEmail(to, next);
        }

        const legalRedirect = mustRedirectLegalAccept(to, store.state.auth.user);
        if (legalRedirect) {
            return next(legalRedirect);
        }

        return next();
    } catch (error) {
        console.error('Authentication check failed', error);
        return handleAuthError(to, next);
    }
});

// Só depois da rota confirmada e do DOM atualizado (chrome + view juntos).
// Não liberar loading no beforeResolve: ainda é anterior ao update do DOM e causava flash no logout.
router.afterEach((to) => {
    setLayout(to);
    store.commit('setLoading', false);
});

// Tratamento global de erros (chunk/CSS 404 após deploy → recarrega uma vez)
router.onError((error) => {
    if (handleDynamicImportChunkError(error)) {
        store.commit('setLoading', false);
        return;
    }
    console.error('Erro de navegação:', error);
    store.commit('setLoading', false);
});

function requiresAuth(to) {
    return to.matched.some(record => record.meta.requiresAuth);
}

function setLayout(to) {
    const layout = to.meta?.layout || 'app';
    store.commit('setLayout', layout === 'auth' ? 'auth' : 'app');
}

function redirectToLogin(to, next) {
    next({ name: 'login', query: { redirect: to.fullPath } });
}

function needsConfirmeEmail(to) {
    return !store.getters['auth/isConfirmatedEmail'] && to.name !== 'confirm-email-boxed';
}

function redirectToConfirmeEmail(to, next) {
    next({ name: 'confirm-email-boxed', params: { redirect: to.fullPath } });
}

function needsRegistrationCompletion(to) {
    return !store.getters['auth/isCompleteRegistration'] && to.name !== 'complet-register';
}

function redirectToCompleteRegistration(to, next) {
    next({ name: 'complet-register', params: { redirect: to.fullPath } });
}

function handleAuthError(to, next) {
    next({ name: 'login', query: { redirect: to.fullPath } });
}

/**
 * Bloqueia rotas autenticadas até aceitar Termos + Privacidade vigentes na API (LGPD / versionamento).
 */
function mustRedirectLegalAccept(to, user) {
    if (!user || typeof user !== 'object') {
        return null;
    }
    const legal = user.legal;
    if (!legal?.consents_active || !legal?.blocking_enforced) {
        return null;
    }
    const allowed = new Set([
        'aceite-legal',
        'termos-de-servico',
        'politica-de-privacidade',
    ]);
    if (allowed.has(to.name)) {
        return null;
    }
    return { name: 'aceite-legal', query: { redirect: to.fullPath } };
}

export default router;